import { useNavigate, useSearchParams } from 'react-router-dom';
import { NotificationInbox, Page, PageHeader } from '@beyond-js/ui/react';
import { Copies, Origin, useFrame } from '@snapshots/navigation';
import { usePresence } from '@snapshots/session';

/**
 * The person's whole suite inbox: every product's notices, unread or all, by product, with paging.
 * The filter lives in the address, so a reload, a shared link and back and forward keep it. Opening
 * an item re-checks permission at its product; a destination in this interface is followed here.
 * Reached by moving inside the application, focus lands on its heading. It is one family `Page` (D52)
 * of the list template at the fluid width.
 */
export function Notifications() {
	const { text, notices, language } = usePresence();
	const { arrival } = useFrame();
	const navigate = useNavigate();
	const [parameters, setParameters] = useSearchParams();
	const labels = Copies.notifications(language);
	const state = { state: parameters.get('state') === 'all' ? 'all' : 'unread', product: parameters.get('product') ?? '' } as const;

	const open = (destination: string) => {
		const url = new URL(destination, window.location.href);
		if (url.origin === window.location.origin) navigate(`${url.pathname}${url.search}${url.hash}`);
		else window.location.assign(destination);
	};

	return (
		<Page template="list" width="fluid" arrival={<Origin />} header={<PageHeader title={text('notifications.title')} headingRef={arrival.heading} />}>
			<NotificationInbox
				adapter={notices}
				products={Copies.products}
				locale={language}
				labels={labels}
				state={state}
				onState={next => {
					const query = new URLSearchParams();
					if (next.state === 'all') query.set('state', 'all');
					if (next.product) query.set('product', next.product);
					setParameters(query);
				}}
				onOpen={open}
			/>
		</Page>
	);
}
